import { getShapeTextRun } from '@src/js/utils';
import { IAnimQueueItem, IAnimationEditorProps, IAnimationEditorService, IAnimationEditorServiceDelegates, IDeleteSelectedElementAnimation, IDeleteSelectedSlideAnimation, IDeleteSlideAnimation } from '@src/slide/application/service/animation-editor-manager/core/interface';
import AnimationEditorModel from '@src/slide/application/service/animation-editor-manager/core/model';
import { IAnimListItem } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { ifElementSupportAnim } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/attachment';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import BaseViewService from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-service';
import { IObservableArray, IReactionDisposer, reaction } from 'mobx';
import styles from '@src/framework/frame/components/animation-editor/index.less';
import { getElementDetailDisplayInfo } from '@src/utils/get-display-name';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import { REPORT_MAP, ANIM_REPORT, ANIMATION_EDITOR_REF_ID } from '@src/framework/frame/components/animation-editor/constants';
import { i18n } from '@src/slide/application/service/edit-extension/edit-extension-export';
import { ComponentAnimWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-anim-wrapper';
import { getTargetComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-component-relate';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { AnimEventType, EMPH_ANIM_LIST, ENTR_ANIM_LIST, EXIT_ANIM_LIST } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import { AnimType, DEFAULT_CLICK_SPID, DataType, ISlideDataModel, K_DEFAULT_TRIGGER_ID, SlideDataModel, STTLTimeNodeType } from '@tencent/slide-sdk-model';
import { DefaultEvents, Emitter, createNanoEvents } from 'nanoevents';
import { ADD_ANIMATION_TYPE_VALUE, ADD_SLIDE_RANGE_TYPE_VALUE, addSlideAnimationValue, CUSTOM_ELEMENT_TYPE, CUSTOM_ELEMENT_TYPE_VALUE, MODEL_SWITCH, SIMPLE_AND_FASHIONABLE_STRATEGY, SMART_JUMP_DELAY, SMART_JUMP_PICTURE_AND_TABLE_STRATEGY } from '@src/framework/workbench/ui/batch-handle-animation/constants';
import { transaction } from '@src/slide/domain/slide-app';
import { TimingPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/page/timing-property-converter';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IAnimCategoryManager } from '../../anim-category-manager/interface';
import { DrawerName, WbEventNames } from '@framework/workbench/drawer/constants';
import {
    IDrawerStateManager,
} from '@src/slide/application/service/drawer-state-manager/core/drawer-state-manager.interface';
import DrawerTopTab from '@framework/workbench/drawer/components/drawer-top-tab';
import AnimationEditor from '@com/animation-editor';
import { DRAWER_HEIGHT } from '@src/slide/application/service/drawer-state-manager/core';
import React from 'react';
import { DrawerService, EventEmitter, InjectorService } from '@tencent/workbench';
import type { Subscription } from 'rxjs';


type ILastSelectInfo = {
    queueIndex: number;
    animIndex: number;
};

type IDragPosition = ILastSelectInfo|null;

export class AnimationEditorService extends BaseViewService<IAnimationEditorProps>
    implements IAnimationEditorService {
    public events: Emitter<DefaultEvents> = createNanoEvents();
    declare protected model: AnimationEditorModel;

    private services: IAnimationEditorServiceDelegates;

    private updateContainerClassTimer: NodeJS.Timer;
    private animListLength: number;
    private dragPosition: IDragPosition;
    private lastSelectInfo: ILastSelectInfo;
    private mounted: boolean;
    private refreshAction: IReactionDisposer;
    private triggerReaction: IReactionDisposer;
    private animCategoryManager: IAnimCategoryManager;
    private animationReaction: IReactionDisposer;
    private drawerStateManager?: IDrawerStateManager;
    private showAnimation: boolean;
    private drawerService: DrawerService;
    private eventEmitter: EventEmitter;
    private rightDrawerOpenListener: Subscription | undefined;

    public constructor() {
        super();
        this.dragPosition = null;
        this.lastSelectInfo = { queueIndex: 0, animIndex: -1 };
    }

    public setServiceDelegates = (services: IAnimationEditorServiceDelegates) => {
        this.services = {
            ...this.services,
            ...services,
        };
    };

    public init = (drawerStateManager?: IDrawerStateManager) => {
        this.drawerStateManager = drawerStateManager;
        this.drawerService = InjectorService.get(DrawerService);
        this.eventEmitter = InjectorService.get(EventEmitter);
        this.animationReaction = reaction(
            () => this.services.rightSideBarManager?.isAnimationEditorShown,
            (show) => {
                if (!show) {
                    this.drawerStateManager?.closeRightDrawer(DrawerName.ANIMATION_RIGHT_DRAWER);
                }
                this.drawerStateManager?.resetSelectWindowActions();
                this.handleShowAnimationDrawer();
            },
        );
    };

    public componentDidMount = () => {
        this.mounted = true;
        this.refreshAction = reaction(() => {
            const selectionService = this.getSelectionService();
            const { currentSlide } = selectionService;
            return {
                animList: currentSlide ? currentSlide.animList : ([] as unknown as IObservableArray<IAnimListItem>),
                animListLength: currentSlide?.animList ? currentSlide.animList.length : 0,
                hasAnimSupportElement:
                    this.filterAnimSupportElements(selectionService.selectedElements.selectedArray).length > 0,
            };
        }, (obj) => {
            if (this.mounted) {
                this.updateModel<AnimationEditorModel>({
                    animList: obj.animList,
                    hasAnimSupportElement: obj.hasAnimSupportElement,
                });
            }
        }, { fireImmediately: true });

        // 一旦元素文本/数量有改变，会触发动画窗格刷新
        this.triggerReaction = reaction(
            () => {
                const selectionService = this.getSelectionService();
                return (selectionService.currentSlide
                    ? selectionService
                        .currentSlide.children.map(shapeModel => getShapeTextRun(shapeModel))
                    : []);
            },
            () => {
                this.updateModel<AnimationEditorModel>({});
            },
            { fireImmediately: true },
        );
        this.services.animCategoryManager.promise().then((manager) => {
            this.animCategoryManager = manager;
        });
    };

    public componentWillUnmount = () => {
        this.mounted = false;
        this.refreshAction?.();
        this.triggerReaction?.();
    };

    public componentDidUpdate = () => {
        this.updateContainerClassTimer && clearTimeout(this.updateContainerClassTimer);
        this.updateContainerClassTimer = setTimeout(() => {
            this.handleContainerClass();
        }, 200);
    };

    public getAnimListLength = () => this.animListLength;

    public setAnimListLength = (length: number) => {
        this.animListLength = length;
    };

    public clearDragPosition = () => {
        this.dragPosition = null;
    };

    public isDragHover = (animModel, queueIndex) => {
        const info = this.findAnimIndexFromAnimList(animModel, queueIndex);
        return isEqual(info, this.dragPosition);
    };

    /**
     * 计算分隔符对应的animation位置
     * 如果 animModel 不存在，则是队首
     * 如果 animModel 存在，则插在下一个 animModel 前
     * @param {*} animModel
     * @param {*} queueIndex
     */
    public saveDragPositionChange = (animModel, queueIndex) => {
        if (animModel) {
            const dragPosition = this.findAnimIndexFromAnimList(animModel, queueIndex);
            this.dragPosition = { ...dragPosition, animIndex: dragPosition.animIndex + 1 };
        } else {
            const [firstAnim] = this.model.animList[queueIndex].data;
            this.dragPosition = this.findAnimIndexFromAnimList(firstAnim, queueIndex);
        }
    };

    public handleVisibleChange = (animModel, visible) => {
        this.updateModel<AnimationEditorModel>({
            unfoldItemKey: visible ? animModel.key : null,
        });
    };

    public playAnimation = (animation) => {
        this.getAnimPreviewManager()?.previewOneToOneAnims([animation]);
        this.tdwReport(REPORT_MAP.playAnim, animation);
    };

    public deleteAnimation = (animation) => {
        this.getSelectionService().currentSlide?.removeAnimation(animation);
        this.events.emit(AnimEventType.MODIFY_ANIM_TYPE, { animType: '' });
        this.getSelectedAnims()?.remove(animation);
        this.tdwReport(REPORT_MAP.deleteAnim, animation);
    };

    public modifyAnimation = (animation, param, queueIndex) => {
        const animList = this.getSelectionService().currentSlide?.animList;
        const index = animList?.[queueIndex].data.indexOf(animation);
        if (index === -1) {
            return;   // onBlur这类异步操作可能会导致已经切页了， 还在回调修改的问题
        }
        this.getSelectionService().currentSlide?.modifyAnimation(animation, param);
        if (param.animType) {
            this.events.emit(AnimEventType.MODIFY_ANIM_TYPE, { animType: param.animType });
        }
        if (param.animType || param.subType || param.by) {
            this.getAnimPreviewManager()?.previewOneToOneAnims([animation]);
        }
    };

    public moveAnimation = (animModel) => {
        if (this.dragPosition) {
            const fromClickSpid = this.model.animList[this.lastSelectInfo.queueIndex].clickSpid;
            const fromIndex = this.lastSelectInfo.animIndex;
            const toClickSpid = this.model.animList[this.dragPosition.queueIndex].clickSpid;
            const toIndex = this.getToIndex(fromClickSpid, toClickSpid, fromIndex);
            if ((!isEqual(fromClickSpid, toClickSpid) || !isEqual(fromIndex, toIndex)) && !isUndefined(toIndex)) {
                this.getSelectionService()
                    .currentSlide?.moveAnimation(fromIndex, toIndex, fromClickSpid, toClickSpid);
                this.lastSelectInfo = this.findAnimIndexFromAnimList(animModel, this.dragPosition.queueIndex);
                this.tdwReport(REPORT_MAP.dragAnim);
            }
        }
        this.dragPosition = null;
    };

    public calcAnimItemSty = (queue, group, groupIndex, animModelIndex) => {
        const radiusPx = 4;
        const isFirst = animModelIndex === 0;
        const isLast = animModelIndex === group.length - 1;
        const borderBottomRadius = isLast ? radiusPx : 0;
        const borderTopRadius = isFirst ? radiusPx : 0;
        return {
            marginTop: groupIndex && isFirst ? 0 : -1,
            borderTopLeftRadius: borderTopRadius,
            borderTopRightRadius: borderTopRadius,
            borderBottomLeftRadius: borderBottomRadius,
            borderBottomRightRadius: borderBottomRadius,
        };
    };

    public calcIISty = (group, animModelIndex) => {
        const isLast = animModelIndex === group.length - 1;
        return {
            marginBottom: isLast ? 0 : -8,
            zIndex: 999,
        };
    };

    public getTriggerSelectOptions = () => {
        const defaultOptions = [{
            name: i18n.t('无'),
            value: DEFAULT_CLICK_SPID,
        }];
        const { currentSlide } = this.getSelectionService();
        if (!currentSlide) {
            return defaultOptions;
        }
        const selectOptions = currentSlide.children.map(shapeModel => ({
            name: getElementDetailDisplayInfo({ elementModel: shapeModel }).name,
            value: shapeModel.id || '',
        }));
        return defaultOptions.concat(selectOptions).filter(i => !!i.name);
    };

    public selectAnimation = ({ metaKey, ctrlKey, shiftKey }, animModel, queueIndex) => {
        const selectedAnims = this.getSelectedAnims();
        const selectionService = this.getSelectionService();
        selectionService.selectedElements.clear();
        const selectInfo = this.findAnimIndexFromAnimList(animModel, queueIndex);
        if (metaKey || ctrlKey) {
            this.keyDownControl(animModel, selectedAnims);
            this.lastSelectInfo = selectInfo;
        } else if (shiftKey) {
            this.keyDownShift(selectInfo, selectedAnims);
        } else {
            if (selectedAnims?.length !== 1 || !selectedAnims.findItem(animModel)) {
                selectedAnims?.clear();
                selectedAnims?.add(animModel);
            }
            this.lastSelectInfo = selectInfo;
        }
    };

    // 3d 形状渲染问题，暂时屏蔽百叶窗动画入口
    public filterBlindsAnimationFor3DModels = () => {
        const animLists = [
            ENTR_ANIM_LIST,
            EMPH_ANIM_LIST,
            EXIT_ANIM_LIST,
        ];
        const selectedElements = this.getSelectionService().selectedElements.selectedArray;
        animLists.forEach((list) => {
            list.forEach((item) => {
                if (
                    selectedElements?.some(element => element.scene3d || element.shape3d)
                    && item.id
                    && [AnimType.EntrBlinds, AnimType.ExitBlinds].includes(item.id)
                ) {
                    // @ts-ignore
                    // eslint-disable-next-line no-param-reassign
                    item.disabled = true;
                }
            });
        });
    };

    public previewAllAnimation = () => {
        this.getAnimPreviewManager()?.previewFromBeginning();
        this.tdwReport({
            ...REPORT_MAP.playAll,
            ver5: this.animListLength,
            ver6: '',
            ver7: '',
        });
    };

    public getAnimQueues = (animList: IObservableArray<IAnimListItem>) => {
        const animQueue: IAnimQueueItem[] = [];
        animList.forEach(({ data, clickSpid, sequenceTypes }) => {
            const isMainSeq = this.isMainSeq(clickSpid);
            const slideModel = this.getSelectionService().currentSlide;
            const animQueueItem = {
                data: slideModel?.getAnimationClickGroup({ data, clickSpid, sequenceTypes }),
                sequenceTypes,
                title: '',
                canEdit: true,
                isMainSeq,
            };
            if (isMainSeq) {
                animQueue.unshift(animQueueItem);
            } else {
                const elementModel = this.getSelectionService().currentSlide?.children
                    .find(({ id }) => id === clickSpid);
                animQueueItem.title = getElementDetailDisplayInfo({ elementModel }).name;
                animQueue.push(animQueueItem);
            }
        });
        return animQueue;
    };

    public addAnimation = (animType: string, presetClass: string, isFromRecent?: boolean) => {
        this.hideDropDown();
        const selectionService = this.getSelectionService();
        const selectedElements = this.filterAnimSupportElements(selectionService
            .selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE));
        if (get(selectedElements, 'length', undefined) === 0) {
            return;
        }
        if (this.getDisableAnimList(selectedElements).includes(animType as AnimType)) {
            Snackbar.show({
                message: i18n.t('选中元素中包含不支持此动画的元素'),
                autoClose: true,
                closable: true,
                duration: 5000,
            });
            return;
        }
        this.updateElement(selectedElements);
        const report: any = {
            ...REPORT_MAP.addAnim,
            ver7: this.selectedModel?.type,
        };
        let ver6: null | string = null;
        if (animType) {
            report.ver5 = presetClass;
            ver6 = animType;
        }
        this.addCanvasAnimWrapper(selectedElements);
        selectionService.currentSlide?.addAnimation(selectedElements, animType || '');

        // 添加动画默认是给主队列动画添加
        const animList = selectionService.currentSlide?.animList;
        const mainSeq = animList?.find(item => item.clickSpid === DEFAULT_CLICK_SPID);
        if (mainSeq) {
            const newAddAnimModel = mainSeq.data.slice(-selectedElements.length);
            const lastOne = mainSeq.data[mainSeq.data.length - 1];
            this.updateModel<AnimationEditorModel>({ unfoldItemKey: lastOne.key });
            lastOne.needScroll = true;

            const animPreviewManager = this.getAnimPreviewManager();
            const allowAutoPreview = animPreviewManager?.getAutoPreview();

            if (mainSeq.data.length === selectedElements.length
            && !selectionService.currentSlide?.sdkData?.timing) {
                allowAutoPreview && animPreviewManager?.asyncPreviewWithAnims(newAddAnimModel);
            } else {
                allowAutoPreview && animPreviewManager?.previewOneToOneAnims(newAddAnimModel);
            }
            this.getSelectedAnims()?.clear();
            this.getSelectedAnims()?.add(...newAddAnimModel);
        }


        const ver8 = !!isFromRecent;
        this.tdwReport({ ...report, ver6, ver8 });
    };

    /**
     * 删除单页幻灯片动画
     * @param slideDataModel
     * @param deletePresetClassList
     * @param shapeTargetId
     * @returns
     */
    public deleteSlideAnimation({
        slideDataModel,
        shapeTargetId,
        deletePresetClassList = [],
    }: IDeleteSlideAnimation): void {
        if (!slideDataModel) return;
        transaction(() => {
            const unitTimeNodeMap = slideDataModel.timing?.props.unitTimeNodeMap;
            if (!unitTimeNodeMap) return;
            Object.entries(unitTimeNodeMap).forEach(([triggerId, value]) => {
                const { unitTimeNodeList } = value ?? {};
                if (!unitTimeNodeList) return;
                for (let i = unitTimeNodeList.length - 1; i >= 0; i--) {
                    const presetClass = unitTimeNodeList[i].par?.cTn?.presetClass;
                    // shapeTargetId非空时获取当前动画的spId，否则保证shapeTargetId与当前动画spId一致
                    const spId = shapeTargetId
                        ? this.findSpid(unitTimeNodeList[i].par?.cTn?.childTnLst?.egTimeNodeChoice)
                        : shapeTargetId;
                    // presetClass为空或不是需要删除的动画类型或shapeTargetId与当前动画spId不同则跳过删除
                    if (!presetClass || !deletePresetClassList.includes(presetClass)
                        || !(shapeTargetId === spId)) continue;
                    slideDataModel.timing?.api.removeAnimation(TimingPropertyConverter.getSdkTriggerId(triggerId), i);
                }
            });
        });
    }

    /**
     * 删除选择元素动画
     * @param presentationDataModel
     * @param deletePresetClassList
     * @param shapeModels
     * @returns
     */
    public deleteSelectedElementAnimation({
        shapeModels = [],
        deletePresetClassList = [],
    }: IDeleteSelectedElementAnimation): void {
        transaction(() => {
            // 删除选中元素动画
            shapeModels.forEach((shapeModel) => {
                const slideDataModel = shapeModel.parent?.sdkData;
                const shapeTargetId = shapeModel.id;
                if (!shapeTargetId) return;
                this.deleteSlideAnimation({
                    slideDataModel: slideDataModel as SlideDataModel,
                    deletePresetClassList,
                    shapeTargetId,
                });
            });
        });
    }

    /**
     * 删除选中页动画 支持单页/多页/全部
     * @param slideDataList
     * @param deletePresetClassList
     * @returns
     */
    public deleteSelectedSlideAnimation({
        slideList,
        deletePresetClassList = [],
    }: IDeleteSelectedSlideAnimation): void {
        transaction(() => {
            if (!slideList) return;
            slideList.forEach((slideDataModel: ISlideDataModel) => {
                this.deleteSlideAnimation({ slideDataModel, deletePresetClassList });
            });
        });
    }

    /**
     * 添加单页幻灯片动画
     * @param slide
     * @param animationStyle
     * @param animType
     */
    public addSlideAnimation = (
        slide: ISlideDataModel,
        animationStyle: string,
        animType: AnimType[],
        slideListLength: number,
    ) => {
        // 动画侧边栏中元素包括按照文字、形状、其他、图片、表格
        const isPictureOrTable = (typeId: number) => typeId === CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.IMAGE]
            || typeId === CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.TABLE];
        // 按照预设列表重新排序元素
        const reSortedElement = slide.shapeList.slice().sort((a, b) => this.getElementType(a) - this.getElementType(b));
        // 保存上报动画类型
        const animTypeList: AnimType[] = [];
        // 根据元素顺序依次添加动画
        reSortedElement.forEach((shapeModel, index) => {
            const shapeIds: string[] = [shapeModel.id];
            const mainSeqLength = this.getMainSeqLength(slide);
            const elementTypeId = this.getElementType(shapeModel);
            const isPictureOrTableType = isPictureOrTable(elementTypeId);
            const nodeType = index === 0 ? STTLTimeNodeType.clickEffect : STTLTimeNodeType.withEffect;
            const curAnimType = this.getAnimType(animType, isPictureOrTableType, animationStyle);
            const addAnimationParams = {
                shapeIds,
                nodeType,
                index: mainSeqLength,
                triggerId: K_DEFAULT_TRIGGER_ID,
                animType: curAnimType,
                delay: this.getDelay(isPictureOrTableType, animationStyle),
            };
            animTypeList.push(curAnimType);
            slide.timing?.api.addAnimation(addAnimationParams);
        });
        // 上报数据
        this.addSlideAnimationTdwReport(slide, slideListLength, animationStyle, animTypeList);
    };

    /**
     * 添加选中页动画 支持单页/多页/全部
     * @param slideList
     * @param animationStyle
     * @param unifiedStyle
     */
    public addSelectedSlidesAnimation = (
        slideList: ISlideDataModel[],
        animationStyle: string,
        unifiedStyle: AnimType,
    ) => {
        const submitHandles = {
            // 简约风尚
            [ADD_ANIMATION_TYPE_VALUE.SIMPLE_AND_FASHIONABLE]: (slide: ISlideDataModel, index: number) => {
                this.addSlideAnimation(
                    slide,
                    animationStyle,
                    [SIMPLE_AND_FASHIONABLE_STRATEGY[index % 5]],
                    slideList.length,
                );
            },
            // 统一风格
            [ADD_ANIMATION_TYPE_VALUE.UNIFIED_STYLE]: (slide: ISlideDataModel) => {
                this.addSlideAnimation(slide, animationStyle, [unifiedStyle], slideList.length);
            },
            // 灵动跳跃
            [ADD_ANIMATION_TYPE_VALUE.SMART_JUMP]: (slide: ISlideDataModel, index: number) => {
                this.addSlideAnimation(
                    slide,
                    animationStyle,
                    SMART_JUMP_PICTURE_AND_TABLE_STRATEGY[index % 5],
                    slideList.length,
                );
            },
            // 更多策略可以通过修改配置项添加
        };
        transaction(() => {
            slideList.forEach((slide, index) => {
                const defaultHandler = () => {};
                (submitHandles[animationStyle] ?? defaultHandler)(slide, index);
            });
        });
    };

    public getSelectionService = () => this.services?.selectionService;

    public close = () => {
        this.handleDrawerClose();
        this.drawerStateManager?.closeTab(DrawerName.ANIMATION_RIGHT_DRAWER);
    };


    protected createViewModel(): BaseViewModel<IAnimationEditorProps> {
        return new AnimationEditorModel();
    }

    protected getInitModelValue(): Partial<AnimationEditorModel> | undefined {
        const selectedArray = this.services?.selectionService.selectedElements.selectedArray;
        const selectionService = this.getSelectionService();
        return {
            animList: selectionService.currentSlide
                ? selectionService.currentSlide.animList
                : ([] as unknown as IObservableArray<IAnimListItem>),
            hasAnimSupportElement: this.filterAnimSupportElements(selectedArray).length > 0,
        };
    }

    private handleShowAnimationDrawer = () => {
        const show = this.services.rightSideBarManager?.isAnimationEditorShown;
        if (show) {
            this.drawerStateManager?.openTab(DrawerName.ANIMATION_RIGHT_DRAWER);
            this.services.rightSideBarManager?.setIsPropertyEditorShown(false);
            this.services.selectionService.currentSlide?.convertAnimDataSdkToUi();
            if (this.showAnimation) {
                this.closeRightSideBar();
                return;
            }
            this.closeRightSideBarWhenDrawerOpen();
            this.changeShowState();
            this.showAnimationDrawer();
        } else {
            this.handleCloseDrawer();
        }
    };

    private handleCloseDrawer = () => {
        this.changeShowDetailState(false);
        const isAnimationEditorShown = this.services.rightSideBarManager?.isAnimationEditorShown;
        if (isAnimationEditorShown) return;
        window.dispatchEvent(new Event('resize')); // 主动触发window resize
    };

    private showAnimationDrawer = () => {
        this.drawerService.showRightDrawer({
            name: DrawerName.ANIMATION_RIGHT_DRAWER,
            content: <DrawerTopTab><AnimationEditor
                getAnimationEditorService={() => this} /></DrawerTopTab>,
            onClose: this.handleDrawerClose,
        });
        this.drawerStateManager?.handleDrawerHolderVisiable(true);
    };

    private handleDrawerClose = () => {
        this.drawerStateManager?.handleDrawerHolderVisiable(false);
        if (this.isOtherDrawerOpen()) {
            this.rightDrawerOpenListener = this.eventEmitter.listen(WbEventNames.RIGHT_DRAWER_CLOSED, () => {
                this.showAnimationDrawer();
                this.rightDrawerOpenListener?.unsubscribe();
            });
            return;
        }
        this.drawerStateManager?.closeRightDrawer(DrawerName.ANIMATION_RIGHT_DRAWER);
        this.services.rightSideBarManager?.setIsAnimationEditorShown(false);
    };

    private isOtherDrawerOpen = (): boolean | undefined => this.services.rightSideBarManager?.isAnimationEditorShown
        && !(this.drawerService.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.COMMENT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER)
        )
        && this.drawerService.isRightOpening();

    private changeShowState() {
        this.changeShowDetailState(true);
        window.dispatchEvent(new Event('resize')); // 主动触发window resize
    }

    private changeShowDetailState = (isShown: boolean) => {
        this.showAnimation = isShown;
    };

    /**
     * 侧边栏从关闭到打开时关闭sideBar
     */
    private closeRightSideBarWhenDrawerOpen = () =>  {
        this.closeRightSideBar();
        this.services.rightSideBarManager?.setIsSelectWindowShown(false);
    };

    private closeRightSideBar = () => {
        this.services.rightSideBarService?.hide();
    };

    private isMainSeq = clickSpid => clickSpid === DEFAULT_CLICK_SPID;

    /**
     * 获取当前Slide主队列动画长度
     * @param slide
     * @returns
     */
    private getMainSeqLength = (slide: ISlideDataModel) => {
        const list = slide.timing?.props?.unitTimeNodeMap[K_DEFAULT_TRIGGER_ID]?.unitTimeNodeList;
        return list?.length ?? 0;
    };

    /**
     * 获取元素的类型 支持文字、形状、其它、图片和表格5种类型
     * @param element
     * @returns
     */
    private getElementType = (element) => {
        const typeMap = {
            // 文本框和形状
            [DataType.Shape]: element.props.nvPr?.txBox
                ? CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.TEXT]
                : CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.SHAPE],
            // 图像、音频和视频
            [DataType.Picture]: element.props.video || element.props.audio
                ? CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.OTHER]
                : CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.IMAGE],
            // 表格
            [DataType.GraphicalObjectFrameTable]: CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.TABLE],
        };
        // 除了上述形状外剩余都归其它形状
        return typeMap[element.type] ?? CUSTOM_ELEMENT_TYPE_VALUE[CUSTOM_ELEMENT_TYPE.OTHER];
    };

    /**
     * 批量添加动画根据元素和风格获取对应动画类型
     * @param animType
     * @param isPictureOrTableType
     * @param animationStyle
     * @returns animType[]
     */
    private getAnimType = (animType: AnimType[], isPictureOrTableType: boolean, animationStyle: string) =>
        // 元素类型是表格或图片，且动画风格是灵动跳跃，则获取动画类型数组第第二个元素否则获取第一个元素
        animType[isPictureOrTableType && animationStyle === ADD_ANIMATION_TYPE_VALUE.SMART_JUMP
            ? MODEL_SWITCH.SIMPLE_AND_FASHIONABLE : MODEL_SWITCH.OTHER] as AnimType;

    /**
     * 批量添加动画根据元素和风格获取对应动画延迟
     * @param isPictureOrTableType
     * @param animationStyle
     * @returns delay
     */
    private getDelay = (isPictureOrTableType: boolean, animationStyle: string) =>
        (isPictureOrTableType && animationStyle === ADD_ANIMATION_TYPE_VALUE.SMART_JUMP
            ? SMART_JUMP_DELAY[CUSTOM_ELEMENT_TYPE.IMAGE] : SMART_JUMP_DELAY[CUSTOM_ELEMENT_TYPE.OTHER]);

    // 批量动画-添加成功-tdw上报
    private addSlideAnimationTdwReport = (
        slide: ISlideDataModel,
        slideListLength: number,
        animationStyle: string,
        animType: AnimType[],
    ) => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.ADD_SUCCESS,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_ADD_SUCCESS,
        };
        tdw({
            ...reportInfo,
            ver5: slide.parent.slideList.length === slideListLength
                ? addSlideAnimationValue.ALL_SLIDE
                : addSlideAnimationValue.SELECTED_SLIDE,
            ver6: animationStyle,
            ver7: animType,
        });
    };
    private updateElement = (selectedElements) => {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < selectedElements.length; i++) {
            const element = selectedElements[i];
            if (element?.type === UIModelType.SHAPE) {
                element?.editorInstance?.triggerBlur?.();
            } else if (element?.type === UIModelType.TABLE) {
                element?.uploadEditingCellData();
            }
        }
    };

    private filterAnimSupportElements = (elements) => {
        if (!elements) {
            return [];
        }
        return elements.filter(element => ifElementSupportAnim(element));
    };

    private handleContainerClass = () => {
        const editorBottom = this.viewDelegate?.getElementById(ANIMATION_EDITOR_REF_ID.EDITOR_BOTTOM);
        const scrollContainer = this.viewDelegate?.getElementById(ANIMATION_EDITOR_REF_ID.SCROLL_CONTAINER);
        if (this.model.animList.length === 0) {
            editorBottom?.classList?.add(styles['editor-bottom-padding-top']);
            return;
        }
        if (scrollContainer && scrollContainer?.scrollHeight > scrollContainer?.clientHeight) {
            scrollContainer?.classList?.add(styles.bottomBorder);
            editorBottom?.classList?.add(styles['editor-bottom-padding-top']);
        } else {
            scrollContainer?.classList?.remove(styles.bottomBorder);
            editorBottom?.classList?.remove(styles['editor-bottom-padding-top']);
        }
    };


    // 添加动画时，若原来的元素上没有anim-wrapper，则动态创建anim-wrapper
    private addCanvasAnimWrapper = (selectedElements) => {
        const slideComponent = this.getAnimManager().editSlideComponent;
        if (!slideComponent) return;
        selectedElements.forEach((selectedElementModel) => {
            if (!selectedElementModel) return;
            const elementKey = selectedElementModel?.key;
            const canvasComponentWrapper = getTargetComponent(elementKey, slideComponent);
            if (!canvasComponentWrapper) return;
            const isAnimWrapper = canvasComponentWrapper instanceof ComponentAnimWrapper;
            if (isAnimWrapper) return;
            const originalParent = canvasComponentWrapper?.parent;
            const index = originalParent?.children?.findIndex(child => child === canvasComponentWrapper);
            if (index === -1 || isUndefined(index)) return;
            originalParent.removeChild(canvasComponentWrapper);
            const animWrapper = new ComponentAnimWrapper(
                canvasComponentWrapper.factoryOptions,
                canvasComponentWrapper.factoryDispose,
                canvasComponentWrapper.factoryUpdateComponent,
            );
            animWrapper.addChild(canvasComponentWrapper);
            originalParent.addChildAt(animWrapper, index);
        });
    };

    private keyDownControl = (animModel, selectedAnims) => {
        if (animModel.selected) {
            selectedAnims.remove(animModel);
        } else {
            selectedAnims.add(animModel);
        }
    };

    /**
     * 目前触发器不支持多选，这里简化实现主队列的动画多选
     * @param {*} selectInfo
     */
    private keyDownShift = (selectInfo, selectedAnims) => {
        const { animIndex } = selectInfo;
        const mainSeq = this.model.animList.find(item => item.clickSpid === DEFAULT_CLICK_SPID);
        if (!mainSeq) return;
        if (this.lastSelectInfo.animIndex < 0 || this.lastSelectInfo.animIndex >= mainSeq.data.length) {
            this.lastSelectInfo = selectInfo;
        }
        const min = Math.min(this.lastSelectInfo.animIndex, animIndex);
        const max = Math.max(this.lastSelectInfo.animIndex, animIndex) + 1;
        const animations = mainSeq.data.slice(min, max);
        selectedAnims.clear();
        selectedAnims.add(...animations);
    };

    private findAnimIndexFromAnimList = (animModel, queueIndex) => {
        const animIndex = this.model.animList[queueIndex].data.indexOf(animModel);
        return { queueIndex, animIndex };
    };

    /**
     * 定位拖拽结束的index
     * 当 clickSpid 相同时，证明在同一队列，前往后移动需要-1
     * @param {*} fromClickSpid
     * @param {*} toClickSpid
     * @param {*} fromIndex
     * @returns
     */
    private getToIndex = (fromClickSpid, toClickSpid, fromIndex) => {
        if (fromClickSpid === toClickSpid
            && (this.dragPosition?.animIndex && this.dragPosition.animIndex > fromIndex)) {
            return this.dragPosition.animIndex - 1;
        }
        return this.dragPosition?.animIndex;
    };

    private hideDropDown = () => {
        const dropDown = this.viewDelegate?.getElementById(ANIMATION_EDITOR_REF_ID.DROP_DOWN);
        if (dropDown) {
            dropDown.hide();
        }
    };

    private get selectedModel() {
        return this.services?.selectionService.getSelectedModel();
    }

    private tdwReport = (report, animation: any = {}) => {
        const { props: { animType = '', presetClass = '' } = {}, elementModel: { type = '' } = {} } = animation;
        tdw({ ...ANIM_REPORT, ver5: presetClass, ver6: animType, ver7: type, ...report });
    };

    /**
     * 查找数组中第一个键为spid的值
     * @param egTimeNodeChoice
     * @returns spId
     */
    private findSpid(egTimeNodeChoice) {
        if (typeof egTimeNodeChoice !== 'object') return;
        if ('spid' in egTimeNodeChoice) return egTimeNodeChoice.spid;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of Object.keys(egTimeNodeChoice)) {
            const result = this.findSpid(egTimeNodeChoice[key]);
            if (result !== undefined) return result;
        }
    }


    private getAnimPreviewManager = () => this.services.editorAnimPreviewManager?.();

    private getSelectedAnims = () => this.services.selectedAnims;

    private getAnimManager = () => this.services.animManager;

    private getDisableAnimList(elementModels: Array<IElementBaseModel>): AnimType[] {
        // 动画类型未去重，该场景下不需要去重
        const disableList: AnimType[] = elementModels.reduce((accList, model) => {
            const list = this.animCategoryManager.getDisabledAnimTypeList(model);
            return [...accList, ...list];
        }, []);
        return disableList;
    }
}
